/**
 * F-0581 (`docs/concept-get-started.md`): the part of this device's Get started record that every
 * launch reads — whether the wizard opens, and the one settle before the first render. Kept apart
 * from `deviceGetStarted.ts` (the wizard's draft, its steps and their parsing) so that a launch
 * carries only this in its entry (T-0559); the wizard's own code loads with its route.
 */
import { deviceLocal } from '@/privacy/deviceStorage';
import { appPath } from '@featit/shared/addressing/paths';
import { APP_HOME_PATH } from '@/routing/appHome';
import { THEME_STORAGE_KEY } from '@/theme/themeStorage';

export const GET_STARTED_PATH = '/get-started';
export const DEVICE_GET_STARTED_KEY = 'featit:get-started';

export const DEVICE_GET_STARTED_PHASES = {
  /** The wizard is open on this device and not finished: reopened on the next start. */
  STARTED: 'started',
  /** Finished, or a device from before the wizard: never opened again unless asked for. */
  FINISHED: 'finished',
} as const;

export type DeviceGetStartedPhaseType =
  (typeof DEVICE_GET_STARTED_PHASES)[keyof typeof DEVICE_GET_STARTED_PHASES];

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function deviceStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : deviceLocal;
  } catch {
    return null;
  }
}

/** The record's phase, or null with no record or one that is not readable. */
export function readDeviceGetStartedPhase(
  store: StorageLike | null = deviceStorage(),
): DeviceGetStartedPhaseType | null {
  try {
    const raw = store?.getItem(DEVICE_GET_STARTED_KEY);
    if (!raw) return null;
    const { phase } = JSON.parse(raw) as { phase?: unknown };
    return phase === DEVICE_GET_STARTED_PHASES.STARTED ||
      phase === DEVICE_GET_STARTED_PHASES.FINISHED
      ? phase
      : null;
  } catch {
    return null;
  }
}

export interface ISettleInputs {
  /** Whether this device holds a session, read after a desktop or shell session is restored. */
  hasSession: boolean;
  store?: StorageLike | null;
}

/**
 * Called once at boot, before anything renders (and so before `ThemeProvider` stores a theme).
 * A device with no record of its own that has a stored theme or a session started before: it is
 * recorded as finished. A device with neither is on its first start, and keeps no record until the
 * wizard writes one. `ProtectedRoute` calls it again once a session exists (OB-12).
 *
 * *Mutation (OB-1):* drop the stored-theme check → "a second start without a session goes to the
 * usual sign-in" goes red.
 */
export function settleDeviceGetStarted({
  hasSession,
  store = deviceStorage(),
}: ISettleInputs): void {
  if (readDeviceGetStartedPhase(store)) return;
  let hasTheme = false;
  try {
    hasTheme = Boolean(store?.getItem(THEME_STORAGE_KEY));
  } catch {
    hasTheme = false;
  }
  if (!hasTheme && !hasSession) return;
  try {
    // The finished record as `finishDeviceGetStarted` writes it.
    store?.setItem(
      DEVICE_GET_STARTED_KEY,
      JSON.stringify({ phase: DEVICE_GET_STARTED_PHASES.FINISHED, reached: 'phone', draft: {} }),
    );
  } catch {
    // Storage refused (a private window): the next start settles again.
  }
}

/** OB-1, OB-7: whether this device opens the wizard: on a first start, or to resume it. */
export function deviceOpensGetStarted(store: StorageLike | null = deviceStorage()): boolean {
  const phase = readDeviceGetStartedPhase(store);
  return phase === null || phase === DEVICE_GET_STARTED_PHASES.STARTED;
}

/** OB-7: the wizard was begun on this device and not finished. */
export function deviceGetStartedInProgress(store: StorageLike | null = deviceStorage()): boolean {
  return readDeviceGetStartedPhase(store) === DEVICE_GET_STARTED_PHASES.STARTED;
}

/**
 * Where a start of the app is an address that names no page of its own: the root and the app's
 * home. A link to a particular page (an invitation, a public work area, a settings page from an
 * email) keeps going where it went, so the wizard never stands between a person and a page they
 * were sent to.
 */
export function isAppStartPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/' || path === APP_HOME_PATH || path === appPath('');
}
